
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

interface NewsItem {
  id: number;
  date: string;
  category: string;
  title: string;
  content: string;
}

const newsData: NewsItem[] = [
  {
    id: 1,
    date: '2026.01.01',
    category: 'お知らせ',
    title: 'ホームページ開設のお知らせ',
    content: '自社ホームページを開設いたしました。より良いサービスを提供できるよう、社員一同努めてまいります。'
  }
];

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });
  const contentAnimation = useScrollAnimation({ threshold: 0.2 });

  const newsItem = newsData.find(item => item.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!newsItem) return;

    // SEO meta
    document.title = `${newsItem.title} | 株式会社鈴電エンジニア`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', newsItem.content.slice(0, 120));

    // Schema
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      '@id': `${siteUrl}/news/${newsItem.id}`,
      url: `${siteUrl}/news/${newsItem.id}`,
      headline: newsItem.title,
      description: newsItem.content.slice(0, 120),
      datePublished: newsItem.date.replace(/\./g, '-'),
      dateModified: newsItem.date.replace(/\./g, '-'),
      inLanguage: 'ja',
      publisher: {
        '@type': 'Organization',
        name: '株式会社鈴電エンジニア',
        url: siteUrl,
      },
      isPartOf: { '@id': `${siteUrl}/#website` },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-news-detail';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.title = '株式会社鈴電エンジニア';
      if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアは、電気設備工事を中心に、空調・消防設備工事など、建物に関わる各種設備工事を行っています。');
      const el = document.getElementById('schema-news-detail');
      if (el) el.remove();
    };
  }, [newsItem]);

  if (!newsItem) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">お知らせが見つかりません</h1>
            <button
              onClick={() => navigate('/news')}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              お知らせ一覧に戻る
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="relative py-20 sm:py-24 bg-gradient-to-br from-sky-50 to-white">
        <div 
          ref={heroAnimation.ref as React.RefObject<HTMLDivElement>}
          className={`max-w-7xl mx-auto px-4 sm:px-6 text-center scroll-animate ${heroAnimation.isVisible ? 'is-visible' : ''}`}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4">お知らせ詳細</h1>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div
            ref={contentAnimation.ref as React.RefObject<HTMLDivElement>}
            className={`bg-white rounded-lg shadow-lg p-8 md:p-12 scroll-animate ${contentAnimation.isVisible ? 'is-visible' : ''}`}
          >
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200">
              <span className="text-sm text-gray-500 whitespace-nowrap">{newsItem.date}</span>
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap bg-teal-100 text-teal-700">
                {newsItem.category}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900 leading-tight">
              {newsItem.title}
            </h2>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-wrap">
                {newsItem.content}
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <button
                onClick={() => navigate('/news')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                お知らせ一覧に戻る
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
